import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box } from '@mui/material';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelixPalette, HelixStyles } from '../../../helix';
import { HELIX_REACT_CORE_TRANSLATION_NAMESPACE } from '../../../locales';
import { Button } from '../../Button';
import { IChevronDown, IChevronUp, ICross2, IError, IEye, IMailClosed, INumbers, IView } from '../../Iconography';
import { FieldWrapper } from '../FieldWrapper';
import { HelixInput, HelixTextArea } from './InputField.components';
import './InputField.scss';
/**
 * An feild for various text and number inputs.
 * TODO: Seperate out each input in V2.
 */
export const InputField = forwardRef(({ preventDefaultEnter = false, actionButtons, autoResize = false, allowPasswordToggle = true, contextalTooltip, dataId, disabled = false, error = '', height = 40, helpText = '', id, inputStyle = HelixStyles.style_07, label, max, maxLength, min, onEnter, onChange, placeholder, rightAdornment, readOnly = false, required = false, rows = 6, showClearWhenNotActive = false, showOptional = false, startActive = false, style, type, value = null, variant = 'default', width = '100%', }, ref) => {
    // Hooks.
    const { t } = useTranslation();
    const [active, setActive] = useState(false);
    const [charCountHelperText, setCharCountHelperText] = useState();
    const [showPassword, setShowPassword] = useState(false);
    const [currentAdornment, setCurrentAdornment] = useState(null);
    // Focus states
    const [clearFocused, setClearFocused] = useState(false);
    const [viewPasswordFocused, setViewPasswordFocused] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);
    // Constants.
    const END_ADORNMENT_PADDING = type === 'stepper' ? 20 : 36;
    const inputFieldClass = ['input-field', type === 'stepper' && 'stepper', variant === 'white' && 'white', disabled && 'disabled', error && 'error']
        .filter(Boolean)
        .join(' ');
    // The ref for the input.
    let inputRef = useRef(null);
    // Set the inputRef to the forwarded ref if there is one.
    if (ref)
        inputRef = ref;
    /**
     * Handler called to clear the input field.
     */
    const handleClearButtonOnClick = () => {
        onChange(null);
        if (inputRef.current)
            inputRef.current.focus();
    };
    /**
     * Handler called to show or hide the password.
     */
    const handleShowPasswordClick = () => {
        setShowPassword((prevShowPassword) => {
            const newShowPassword = !prevShowPassword;
            return newShowPassword;
        });
    };
    /**
     * Handler called when the input loses focus.
     * @param e The focus event.
     */
    const handleInputOnBlur = () => {
        // Restrict the input to the min and max values if there are any.
        if ((type === 'number' && value !== null) || (type === 'stepper' && value !== null)) {
            const numValue = +value;
            if (min !== undefined && numValue < min)
                onChange(min);
            else if (max !== undefined && numValue > max)
                onChange(max);
        }
        setInputFocused(false);
    };
    /**
     * Handler called when the field value changes.
     * @param e The change event.
     */
    const handleInputOnChange = (e) => {
        const { target } = e;
        switch (type) {
            case 'number':
            case 'stepper':
                onChange(target.value > '' ? target.valueAsNumber : null);
                break;
            case 'text':
            case 'textarea':
            case 'password':
            case 'email':
                onChange(target.value > '' ? target.value : null);
                break;
        }
    };
    /**
     * Handler called to check each input char to see if it is a number or '-'.
     * @param e The keyboard event.
     */
    const handleNumberInputOnKeyDown = (e) => {
        const blackListKeys = ['e'];
        if (blackListKeys.find((key) => key === e.key)) {
            e.preventDefault();
            e.stopPropagation();
        }
        handleTextInputOnKeyDown(e);
    };
    /**
     * Handler called when key is pressed while fouced on an event key. Used to call custom onEnter function.
     * @param e The keyboard event.
     */
    const handleTextInputOnKeyDown = (e) => {
        //Check if user has passed a custom onEnter function
        if (e.key === 'Enter' && !e.shiftKey && preventDefaultEnter && onEnter) {
            onEnter();
            e.preventDefault();
            e.stopPropagation();
        }
    };
    // Set the active state of the field when the focus state changes.
    useEffect(() => {
        if (viewPasswordFocused || clearFocused || inputFocused)
            setActive(true);
        else {
            setActive(false);
            setShowPassword(false);
        }
    }, [viewPasswordFocused, clearFocused, inputFocused]);
    // Activate the field.
    useEffect(() => {
        if (inputRef.current && startActive)
            inputRef.current.focus();
    }, [inputRef]);
    // Decide on the right adornment to render.
    useEffect(() => {
        let adornment = null;
        //If the type is stepper then show two buttons to increment and decrement the value of the field.
        if (type === 'stepper' && !disabled && !readOnly) {
            adornment = (_jsxs("div", Object.assign({ className: "stepper-adornment" }, { children: [_jsx(Button, { dataId: `${dataId}-stepper-up`, title: t('inputField.increment', { ns: HELIX_REACT_CORE_TRANSLATION_NAMESPACE }), tabIndex: -1, type: "universal", leftIcon: _jsx(IChevronUp, {}), variant: variant === 'white' ? 'white' : 'grey', onClick: () => {
                            if (inputRef.current)
                                inputRef.current.focus();
                            if (value === undefined || value === null) {
                                onChange(min || 0);
                            }
                            else if (typeof max === 'number' && value >= max) {
                                return;
                            }
                            else {
                                onChange(value + 1);
                            }
                        }, size: "small" }), _jsx(Button, { dataId: `${dataId}-stepper-down`, title: t('inputField.decrement', { ns: HELIX_REACT_CORE_TRANSLATION_NAMESPACE }), tabIndex: -1, type: "universal", leftIcon: _jsx(IChevronDown, {}), variant: variant === 'white' ? 'white' : 'grey', onClick: () => {
                            if (inputRef.current)
                                inputRef.current.focus();
                            if (value === undefined || value === null) {
                                onChange(min || 0);
                            }
                            else if (typeof min === 'number' && value <= min) {
                                return;
                            }
                            else {
                                onChange(value - 1);
                            }
                        }, size: "small" })] })));
        }
        // If the input type is password and allowPasswordToggle is true -
        // add button to hide and show password visibility
        else if (type === 'password' && allowPasswordToggle && !disabled && !readOnly)
            adornment = (_jsx(Button, { dataId: `${dataId}-view-password-toggle`, title: showPassword
                    ? t('inputField.hidePassword', { ns: HELIX_REACT_CORE_TRANSLATION_NAMESPACE })
                    : t('inputField.showPassword', { ns: HELIX_REACT_CORE_TRANSLATION_NAMESPACE }), type: "universal", leftIcon: showPassword ? _jsx(IView, {}) : _jsx(IEye, {}), variant: variant === 'white' ? 'white' : 'grey', onBlur: () => setViewPasswordFocused(false), onClick: handleShowPasswordClick, onFocus: () => setViewPasswordFocused(true) }));
        // If it is a text feild and there is a value and the input is active or has focus then display the clear button.
        // Also display clear if the above is true and showClearWhenNotActive is true.
        else if ((type === 'text' && value && !disabled && active) || (type === 'text' && value && !active && !disabled && showClearWhenNotActive))
            adornment = (_jsx(Button, { dataId: `${dataId}-clear-button`, title: t('commands.clear', { ns: HELIX_REACT_CORE_TRANSLATION_NAMESPACE }), type: "universal", leftIcon: _jsx(ICross2, {}), variant: variant === 'white' ? 'white' : 'grey', onBlur: () => setClearFocused(false), onClick: handleClearButtonOnClick, onFocus: () => setClearFocused(true) }));
        // Display the error icon.
        else if (error && !disabled)
            adornment = _jsx(IError, { dataId: `${dataId}-error-icon`, color: HelixPalette.red60 });
        // Display any type specific end adornment.
        else if (type === 'number')
            adornment = _jsx(INumbers, { dataId: `${dataId}-number-icon`, color: variant === 'white' ? HelixPalette.white : HelixPalette.neutral90 });
        else if (type === 'email')
            adornment = _jsx(IMailClosed, { dataId: `${dataId}-email-icon`, color: variant === 'white' ? HelixPalette.white : HelixPalette.neutral90 });
        // Display any right adornment passed in as a prop.
        else if (rightAdornment)
            adornment = rightAdornment;
        setCurrentAdornment(adornment);
    }, [active, disabled, error, value, showPassword, readOnly]);
    // Displays the correct helper text under the field.
    useEffect(() => {
        // Auto size the TextArea.
        if (autoResize && type === 'textarea' && inputRef.current) {
            const { current } = inputRef;
            current.style.height = `auto`;
            if (value)
                current.style.height = `${current.scrollHeight}px`;
        }
        // Work out the helper text.
        let helperTextValue;
        if (!disabled && !readOnly) {
            if (error > '')
                helperTextValue = error;
            else if (value) {
                const charsRemaining = parsedMaxlength - value.length;
                if (charsRemaining <= 20) {
                    helperTextValue =
                        charsRemaining === 1
                            ? t('inputField.charRemaining', { ns: HELIX_REACT_CORE_TRANSLATION_NAMESPACE })
                            : t('inputField.charsRemaining', { chars: charsRemaining, ns: HELIX_REACT_CORE_TRANSLATION_NAMESPACE });
                }
            }
        }
        setCharCountHelperText(helperTextValue);
    }, [value, error]);
    // Set the correct max length property.
    // Sets a default value based on the multiline prop.
    let parsedMaxlength = type === 'textarea' ? 1000 : 256;
    if (maxLength)
        parsedMaxlength = maxLength;
    // Base input props.
    const inputProps = {
        'data-active': active,
        'data-error': error > '',
        disabled,
        id,
        onBlur: handleInputOnBlur,
        onChange: handleInputOnChange,
        onFocus: () => setInputFocused(true),
        placeholder: !disabled ? placeholder : undefined,
        readOnly,
    };
    let input = null;
    switch (type) {
        case 'number':
        case 'stepper':
            input = (_jsx(HelixInput, Object.assign({}, inputProps, { ref: inputRef, "data-id": `${dataId}-input`, max: max, min: min, onKeyDown: handleNumberInputOnKeyDown, type: "number", value: value !== null ? value : '', sx: Object.assign(Object.assign({}, inputStyle), { height, minHeight: '2.5rem', padding: currentAdornment ? `8px ${END_ADORNMENT_PADDING}px 8px 12px` : '8px 8px 8px 12px' }) })));
            break;
        case 'textarea':
            let inputPadding = '12px';
            if (currentAdornment) {
                if (actionButtons) {
                    inputPadding = `12px ${END_ADORNMENT_PADDING}px 8px 12px`;
                }
                else {
                    inputPadding = `12px ${END_ADORNMENT_PADDING}px 12px 12px`;
                }
            }
            else if (actionButtons) {
                inputPadding = `12px 12px 8px 12px`;
            }
            input = (_jsx(HelixTextArea, Object.assign({}, inputProps, { ref: inputRef, "data-id": `${dataId}-textarea`, maxLength: parsedMaxlength, onFocus: (event) => {
                    setInputFocused(true);
                    const lengthOfInput = event.target.value.length;
                    return event.target.setSelectionRange(lengthOfInput, lengthOfInput);
                }, onKeyDown: handleTextInputOnKeyDown, rows: rows, value: value || '', sx: Object.assign(Object.assign({}, inputStyle), { overflow: autoResize ? 'hidden' : 'auto', padding: inputPadding }) })));
            break;
        case 'password':
            input = (_jsx(HelixInput, Object.assign({}, inputProps, { ref: inputRef, "data-id": `${dataId}-input`, autoComplete: "off", maxLength: parsedMaxlength, onKeyDown: handleTextInputOnKeyDown, type: showPassword ? 'text' : 'password', 
                //Do not use value as shown in DOM
                sx: Object.assign(Object.assign({}, inputStyle), { height, minHeight: '2.5rem', padding: currentAdornment ? `8px ${END_ADORNMENT_PADDING}px 8px 12px` : '8px 8px 8px 12px' }) })));
            break;
        default:
            input = (_jsx(HelixInput, Object.assign({}, inputProps, { ref: inputRef, "data-id": `${dataId}-input`, autoComplete: "off", maxLength: parsedMaxlength, type: type === 'email' ? 'email' : 'text', value: value || '', onKeyDown: handleTextInputOnKeyDown, sx: Object.assign(Object.assign({}, inputStyle), { height, minHeight: '2.5rem', padding: currentAdornment ? `8px ${END_ADORNMENT_PADDING}px 8px 12px` : '8px 8px 8px 12px' }) })));
            break;
    }
    return (_jsx(FieldWrapper, Object.assign({ dataId: dataId, error: error > '', helpText: error || helpText || charCountHelperText, id: id, label: label, required: required, width: width, showOptional: showOptional, style: style, variant: variant, contextualTooltip: contextalTooltip }, { children: _jsx(Box, Object.assign({ position: "relative", className: inputFieldClass }, { children: _jsxs(Box, Object.assign({ display: "flex", flexDirection: "column", width: "100%" }, { children: [input, currentAdornment && (_jsx(Box, Object.assign({ alignItems: "center", display: "flex", height: height, left: `calc(100% - ${type === 'textarea' ? 32 : END_ADORNMENT_PADDING}px)`, top: 0, position: "absolute" }, { children: currentAdornment }))), actionButtons && (_jsx(Box, Object.assign({ display: "flex", justifyContent: "flex-end", padding: "8px 12px 12px 12px", gap: "8px" }, { children: actionButtons.map((actionButton) => (_jsx(Button, { dataId: `${dataId}-${actionButton.ariaLabel}`, leftIcon: actionButton.icon, title: actionButton.ariaLabel, type: "universal", variant: "teal", size: "large", onClick: actionButton.onClick, disabled: actionButton.disabled }, actionButton.ariaLabel))) })))] })) })) })));
});
export default InputField;
